// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { AuditPurchaseOrderProperty } from '@/audit/api/types';
import { AuditPropertyValue } from '@/audit/components/audit-property-value';
import { Link } from '@/components/link';
import { ArrowForwardRounded } from '@/constants/hardfin-standard-icons';
import { Typography } from '@mui/material';

interface Props {
  property: AuditPurchaseOrderProperty;
}

interface PurchaseOrderValue {
  key: string;
  poNumber: string;
}

/**
 * Renders a purchase order reference as a link to its detail page. A null
 * reference (the order was cleared) renders as the usual empty value. A bare
 * string is a row written before the field carried an object, so it renders as
 * plain text with nothing to link to.
 */
function PurchaseOrderValueLabel(props: {
  value: PurchaseOrderValue | string | null | undefined;
}) {
  if (!props.value) {
    return <AuditPropertyValue value={null} />;
  }

  if (typeof props.value === 'string') {
    return <AuditPropertyValue value={props.value} />;
  }

  return (
    <Link
      to="/purchase-orders/$purchaseOrderKey"
      params={{ purchaseOrderKey: props.value.key }}
      variant="inherit"
    >
      {props.value.poNumber}
    </Link>
  );
}

export function AuditPurchaseOrderLabel(props: Props) {
  if (props.property.oldValue === undefined) {
    return (
      <Typography display="flex" variant="caption" alignItems="center">
        <Typography component="span" variant="inherit" fontWeight="bold">
          {props.property.name}:&nbsp;
        </Typography>
        <PurchaseOrderValueLabel value={props.property.newValue} />
      </Typography>
    );
  }

  return (
    <Typography display="flex" variant="caption" alignItems="center">
      <Typography component="span" variant="inherit" fontWeight="bold">
        {props.property.name}:&nbsp;
      </Typography>
      <PurchaseOrderValueLabel value={props.property.oldValue} />
      <ArrowForwardRounded fontSize="inherit" sx={{ marginX: 1 }} />
      <PurchaseOrderValueLabel value={props.property.newValue} />
    </Typography>
  );
}
